// Collection quick redirect row ("Showing other collection option for inactive users" CRO,
// approved — rolled out to 100% of mobile web). Markup: snippets/collection-quick-redirect.liquid.
//
// Same behaviour as the test variant:
//   - first time in a group: the row appears once the shopper has scrolled past the 4th product
//     card (that card's bottom above the viewport) — the "inactive" shopper is still browsing;
//   - a group seen before: the row is already shown (inline script in the snippet, before paint);
//   - showing it marks the group as seen (localStorage) and scrolls the active pill into the middle;
//   - a tapped pill remembers its label for the next page (sessionStorage), because two labels
//     can point to the same collection.
// Mobile only (below the theme's md breakpoint, 768px). Tracking:
// assets/collection-quick-redirect-tracking.js (unchanged — it finds #dk-skin-nav).
(() => {
  'use strict';

  const CARDS_TO_SCROLL_PAST = 4;
  const SHOWN_PREFIX = 'dk-skin-nav-shown-';
  const ACTIVE_PREFIX = 'dk-skin-nav-active-';
  const MOBILE_QUERY = '(max-width: 767px)';

  const nav = document.getElementById('dk-skin-nav');
  if (!nav) return;

  const group = nav.dataset.group;
  const normalizePath = (path) => path.replace(/\/$/, '');

  // Remember the tapped label for the next page (works on desktop too — harmless there).
  nav.addEventListener('click', (event) => {
    const pill = event.target.closest('.dk-skin-nav__pill');
    if (!pill) return;
    try {
      sessionStorage.setItem(ACTIVE_PREFIX + normalizePath(pill.pathname), pill.dataset.label || pill.textContent.trim());
    } catch {
      // Storage unavailable — the first matching label is active next time.
    }
  });

  const centerActivePill = () => {
    const active = nav.querySelector('.dk-skin-nav__pill--active');
    if (!active) return;
    nav.scrollLeft = Math.max(0, active.offsetLeft - nav.clientWidth / 2 + active.offsetWidth / 2);
  };

  const markSeen = () => {
    try {
      localStorage.setItem(SHOWN_PREFIX + group, '1');
    } catch {
      // Storage unavailable — it waits for the scroll again next time.
    }
  };

  // Already shown before paint (group seen before): just centre the active pill.
  if (!nav.hidden) {
    markSeen();
    requestAnimationFrame(centerActivePill);
    return;
  }

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  // The row goes in above the cards the shopper is looking at. Browsers with scroll anchoring
  // keep the view still by themselves; others (Safari) are compensated here, so the cards on
  // screen don't jump down by the row's height.
  const supportsAnchoring = window.CSS?.supports?.('overflow-anchor', 'auto') === true;

  const show = () => {
    const below = nav.nextElementSibling;
    const before = below ? below.getBoundingClientRect().top : 0;
    nav.hidden = false;
    if (!supportsAnchoring && below) {
      const shift = below.getBoundingClientRect().top - before;
      if (shift > 0 && before < 0) window.scrollBy(0, shift);
    }
    markSeen();
    requestAnimationFrame(centerActivePill);
  };

  const scrolledPastCards = () => {
    const cards = document.querySelectorAll('[data-product-card]');
    if (!cards.length) return false;
    const target = cards[Math.min(CARDS_TO_SCROLL_PAST, cards.length) - 1];
    return target.getBoundingClientRect().bottom <= 0;
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      if (!nav.hidden) {
        window.removeEventListener('scroll', onScroll);
        return;
      }
      if (!isMobile() || !scrolledPastCards()) return;
      window.removeEventListener('scroll', onScroll);
      show();
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll(); // already scrolled down (e.g. back navigation restored the position)
})();
